import {Injectable} from '@angular/core';
import {ParentService} from "./ParentService";
import {catchError} from "rxjs/operators";
import {HttpErrorResponse} from "@angular/common/http";
import {AiGeneratedQuestionDto} from "../dto/AiGeneratedQuestionDto";
import {AiGeneratedFlashcardDto} from "../dto/AiGeneratedFlashcardDto";
import {AiGenerationPlanDto} from "../dto/AiGenerationPlanDto";
import {AiGenerationJobDto} from "../dto/AiGenerationJobDto";

@Injectable()
export class AiService extends ParentService {

  public getPendingQuestions() {
    return this.http.get<AiGeneratedQuestionDto[]>(this.shared.MAGNAMENT_URL + '/ai/getPendingQuestions/' + this.shared.CORP_ID, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public getReviewedQuestions() {
    return this.http.get<AiGeneratedQuestionDto[]>(this.shared.MAGNAMENT_URL + '/ai/getReviewedQuestions/' + this.shared.CORP_ID, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  /** Aceptar: marca human_reviewed y la pregunta pasa a ser una mas del banco. */
  public reviewQuestion(questionId: number, managerId: number) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/reviewQuestion/' + questionId + '/' + managerId, {}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  /** Rechazar: borra la pregunta y sus respuestas. No hay vuelta atras. */
  public discardQuestion(questionId: number, managerId: number) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/discardQuestion/' + questionId + '/' + managerId, {}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  /**
   * Guarda los retoques del revisor sin resolver la pregunta: sigue en la cola. Se manda el DTO
   * entero porque el backend reescribe enunciado, opciones, correcta, comentario, dificultad y
   * punto clave de una vez.
   */
  public updateQuestion(question: AiGeneratedQuestionDto, managerId: number) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/updateQuestion/' + managerId, question, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  /**
   * Aceptar o rechazar VARIAS de una vez. El backend devuelve cuantas ha hecho y los ids que se
   * han quedado sin tocar, para poder dejar esas filas donde estaban.
   */
  public reviewQuestions(questionIds: number[], managerId: number) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/reviewQuestions/' + managerId, questionIds, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public discardQuestions(questionIds: number[], managerId: number) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/discardQuestions/' + managerId, questionIds, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public reviewFlashcards(flashcardIds: number[], managerId: number) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/reviewFlashcards/' + managerId, flashcardIds, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public discardFlashcards(flashcardIds: number[], managerId: number) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/discardFlashcards/' + managerId, flashcardIds, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  /** Interpreta la peticion y devuelve el plan. No genera nada todavia. */
  public planGeneration(courseId: number, text: string) {
    return this.http.post<AiGenerationPlanDto>(this.shared.MAGNAMENT_URL + '/ai/planGeneration/' + this.shared.CORP_ID, {courseId: courseId, text: text}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  /** Lanza el plan confirmado. Vuelve al momento: la generacion sigue en segundo plano. */
  /**
   * Rehace la cuenta del coste con el plan retocado a mano. No interpreta nada ni gasta IA: es la
   * misma aritmetica del backend, pedida en vez de repetida aqui.
   */
  public estimateGenerationCost(courseId: number, plan: AiGenerationPlanDto, kind?: string) {
    return this.http.post<AiGenerationPlanDto>(this.shared.MAGNAMENT_URL + '/ai/estimateGenerationCost/' + this.shared.CORP_ID, {courseId: courseId, plan: plan, kind: kind}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public launchGeneration(courseId: number, text: string, plan: AiGenerationPlanDto, managerId: number) {
    return this.http.post<AiGenerationJobDto>(this.shared.MAGNAMENT_URL + '/ai/launchGeneration/' + this.shared.CORP_ID + '/' + managerId, {courseId: courseId, text: text, plan: plan}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public getGenerationJobs() {
    return this.http.get<AiGenerationJobDto[]>(this.shared.MAGNAMENT_URL + '/ai/getGenerationJobs/' + this.shared.CORP_ID, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  // ---------------------------------------------------------------- flashcards de IA

  public getPendingFlashcards() {
    return this.http.get<AiGeneratedFlashcardDto[]>(this.shared.MAGNAMENT_URL + '/ai/getPendingFlashcards/' + this.shared.CORP_ID, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public getReviewedFlashcards() {
    return this.http.get<AiGeneratedFlashcardDto[]>(this.shared.MAGNAMENT_URL + '/ai/getReviewedFlashcards/' + this.shared.CORP_ID, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  /** Aceptar: marca human_reviewed y la tarjeta pasa a la baraja de todos los alumnos. */
  public reviewFlashcard(flashcardId: number, managerId: number) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/reviewFlashcard/' + flashcardId + '/' + managerId, {}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  /** Rechazar: borra la tarjeta. No hay vuelta atras. */
  public discardFlashcard(flashcardId: number, managerId: number) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/discardFlashcard/' + flashcardId + '/' + managerId, {}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  /** Guarda los retoques del revisor sin resolver la tarjeta: sigue en la cola. */
  public updateFlashcard(flashcard: AiGeneratedFlashcardDto, managerId: number) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/updateFlashcard/' + managerId, flashcard, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  /**
   * Plan y lanzamiento de flashcards. Son los mismos endpoints que los de preguntas: lo unico que
   * cambia es el kind del cuerpo, que decide si el trabajo genera preguntas o tarjetas.
   */
  public planFlashcardGeneration(courseId: number, text: string) {
    return this.http.post<AiGenerationPlanDto>(this.shared.MAGNAMENT_URL + '/ai/planGeneration/' + this.shared.CORP_ID, {courseId: courseId, text: text, kind: 'FLASHCARD'}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public launchFlashcardGeneration(courseId: number, text: string, plan: AiGenerationPlanDto, managerId: number) {
    return this.http.post<AiGenerationJobDto>(this.shared.MAGNAMENT_URL + '/ai/launchGeneration/' + this.shared.CORP_ID + '/' + managerId, {courseId: courseId, text: text, plan: plan, kind: 'FLASHCARD'}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public getFlashcardGenerationJobs() {
    return this.http.get<AiGenerationJobDto[]>(this.shared.MAGNAMENT_URL + '/ai/getFlashcardGenerationJobs/' + this.shared.CORP_ID, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public ingestLibraries() {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/ingestLibraries/' + this.shared.CORP_ID, {}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public ingestVideos() {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/ingestVideos/' + this.shared.CORP_ID, {}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public seedPrompts() {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/seedPrompts/' + this.shared.CORP_ID, {}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  // Contenidos del curso que se usan (o no) para generar, y contenidos adicionales de la IA.

  public getCourseContents(courseId: number) {
    return this.http.get<any[]>(this.shared.MAGNAMENT_URL + '/ai/courseContents/' + this.shared.CORP_ID + '/' + courseId, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public saveCourseContents(courseId: number, contents: any[]) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/saveCourseContents/' + courseId, contents, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public getExtraContents() {
    return this.http.get<any[]>(this.shared.MAGNAMENT_URL + '/ai/extraContents/' + this.shared.CORP_ID, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public saveExtraContent(title: string, fileUrl: string) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/saveExtraContent/' + this.shared.CORP_ID, {title: title, fileUrl: fileUrl}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public deleteExtraContent(id: number) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/deleteExtraContent/' + id, {}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }
}
